import React, { useState, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { SEO } from '../components/seo';

import { WrappedMenu } from '../components/new/Menu';
import { Footer } from '../components/new/Footer';
import { mobileMQ } from '../components/new/media-utils';
import { CustomBreadCrumbs } from '../components/new/CustomBreadCrumbs';
import { SideNavAndStatus } from '../components/new/SideNavAndStatus';

import Dog from '../images/methodology/inline-svg/dog.svg';
import Grandmother from '../images/methodology/inline-svg/grandmother.svg';
import Lips from '../images/methodology/inline-svg/lips.svg';
import Man from '../images/methodology/inline-svg/man.svg';
import Talk from '../images/methodology/inline-svg/talk.svg';
import externalLinkIcon from '../images/methodology/external-link.svg';

import { Container } from '../components/Container';
import { Wrap } from '../components/new/Wrap';
import { BaseHeadingStyle } from '../components/new/headers';
import { BaseListStyle, H1, Text } from '../components/new/Pages';
import { NewPageWrapper, NewContentWrapper } from '../components/new/wrappers';

const H2 = styled(BaseHeadingStyle)`
    font-size: 40px;
    line-height: 100%;
    margin: 48px 0 16px;

    @media screen and (max-width: 760px) {
        font-size: 28px;
        margin-top: 40px;
    }
`;

export const H3 = styled(BaseHeadingStyle)`
    font-family: 'Circe Web';
    font-size: 24px;
    line-height: 100%;
    margin: 32px 0 16px;

    @media screen and (max-width: 760px) {
        font-size: 20px;
        margin-top: 24px;
    }
`;

const StyledExtLink = styled.a`
    color: #0b9cdb;

    &:after {
        content: '';
        background-image: url(${externalLinkIcon});
        background-repeat: no-repeat;
        background-size: 15px 15px;
        display: inline-block;
        width: 15px;
        height: 15px;
        margin-left: 8px;
    }

    ${mobileMQ(css`
        font-size: 18px;
        line-height: 150%;
    `)}
`;

const HighlightedText = styled(Text)`
    width: 100%;
    background-color: #ccf1fe;
    border-radius: 16px;
    padding: 24px 32px 32px;
    box-sizing: border-box;
    margin: 16px 0 24px;
    color: #404040;

    @media screen and (max-width: 760px) {
        padding: 24px;
        margin: 16px 0;
    }
`;

const VideoWrap = styled.div`
    display: block;
    overflow: hidden;
    border-radius: 16px;
    width: 100%;
    margin-top: 24px;
    height: 309px;

    ${mobileMQ(css`
        height: 159px;
    `)}
`;

const ListWithDash = styled(BaseListStyle)`
    list-style: none;
    padding-left: 10px;
    li:before {
        content: '–';
        position: relative;
        left: -5px;
    }
`;

const MethodologyPage = ({ pageContext }) => {
    const {
        breadcrumb: { crumbs },
    } = pageContext;
    const [refsSet, setRefsSet] = useState(new Set());

    const addToRefs = useCallback(
        el => {
            if (el !== null && !refsSet.has(el)) {
                setRefsSet(refsSet => refsSet.add(el));
            }
        },
        [refsSet]
    );

    return (
        <Wrap>
            <SideNavAndStatus refsSet={refsSet} />
            <SEO title="Методика" description="Методика" />
            <Container>
                <WrappedMenu marginBottom="unset" />
                <NewPageWrapper>
                    <Man />
                    <NewContentWrapper>
                        <H1 as="h1">Особенности методики КЭСПА</H1>
                        <Text as="p">
                            Типичный сценарий изучения английского в
                            русскоязычной стране: начал в школе, продолжил в
                            институте, записался на курсы. Учишь в итоге
                            несколько лет, а можешь только читать, кое-что
                            понимаешь на слух, говоришь очень плохо и с
                            ошибками, а чаще просто молчишь. Знакомо?
                        </Text>
                        <HighlightedText as="div">
                            <Text as="p">
                                В 2019 году только 5% россиян свободно владели
                                английским, тогда как 64% не знали его совсем
                                или ограничивались несколькими фразами, 31% –
                                могли читать, переводить и объясняться в быту.
                            </Text>
                            <Text as="p">
                                <StyledExtLink href="https://wciom.ru/analytical-reviews/analiticheskii-obzor/inostrannyj-yazyk-perspektivnaya-investicziya">
                                    Данные ВЦИОМ
                                </StyledExtLink>
                            </Text>
                        </HighlightedText>
                        <H2 as="h2" id="contemp-methodics" ref={addToRefs}>
                            Современные методики
                        </H2>
                        <Text as="p">
                            Лингвисты выделяют не менее трех десятков методик
                            преподавания языков. У большинства из них, однако,
                            есть нечто общее: они первым делом развивают умение
                            понимать «чужое», переводить с чужого языка на
                            родной. Слова, выражения и грамматические
                            конструкции иностранного языка преподносятся как
                            ключ к его расшифровке. В этом русле ученик
                            постепенно пополняет свой словарный запас и изучает
                            грамматику чужого языка. Это, однако, не означает,
                            что этот ключ так же эффективно может быть
                            использован для обратной коммуникации, «шифровки»
                            своего языка на чужой. Именно поэтому человек с
                            десятилетним багажом знаний остается с «синдромом
                            собаки» – все понимает, но сам сказать не может.
                        </Text>
                        <Dog />
                        <Text as="p">
                            Педагоги стараются решить проблему, применяя
                            разнообразные упражнения. Ученикам предлагают
                            составлять предложения по-английски с использованием
                            новых слов и конструкций или переводить с русского
                            на английский предложения, в которых надо применять
                            полученные знания. Эффективность таких упражнений,
                            как правило, остается низкой. Ученик сможет узнать
                            фразы, встретив их в тексте или в медленной и
                            внятной речи, если педагог их до этого проговаривал.
                            Но спустя несколько недель ученик обычно забывает
                            фразы и не может применить их в разговоре.{' '}
                        </Text>
                        <HighlightedText>
                            Почему это происходит? Согласно модели{' '}
                            <a href="https://ru.wikipedia.org/wiki/%D0%9C%D0%BE%D0%B4%D0%B5%D0%BB%D1%8C_%D0%BF%D0%B0%D0%BC%D1%8F%D1%82%D0%B8_%D0%90%D1%82%D0%BA%D0%B8%D0%BD%D1%81%D0%BE%D0%BD%D0%B0_%E2%80%94_%D0%A8%D0%B8%D1%84%D1%84%D1%80%D0%B8%D0%BD%D0%B0">
                                памяти Аткинсона-Шифрина
                            </a>
                            , в кодировании информации, поступающей в
                            кратковременную память, участвуют в первую очередь
                            зрительные и акустические коды. Еще в 1974 году
                            когнитивный психолог Эндель Тульвинг объяснил это
                            явление в теории «ситуативного забывания». Согласно
                            ей, «невозможность воспроизвести информацию не
                            обязательно означает, что воспоминания потеряны» –
                            эти воспоминания могут быть недоступны из-за
                            «несоответствия между признаками во время
                            кодирования и признаками во время воспроизведения» (
                            <a href="http://yanko.lib.ru/books/psycho/solso=cognitive_psychology-6.ru.pdf">
                                Р. Л. Солсо «Когнитивная психология»
                            </a>
                            ).
                        </HighlightedText>
                        <Text>
                            Еще один распространенный подход к обучению строится
                            на погружении в языковую среду. Но и он имеет
                            ограничения. Вне реальной языковой среды такое
                            погружение организовать проблематично и зачастую оно
                            строится на имитации сценариев – условно-речевых
                            ситуаций («В ресторане», «На приеме у врача», «На
                            собеседовании» и т.п.). Оказавшись за пределами
                            изученного сценария, человек начинает теряться и не
                            знает, что сказать. Погружение же в реальную
                            языковую среду требует значительных финансовых
                            затрат и все равно не гарантирует овладения языком
                            даже за длительный срок – об этом говорят данные
                            американского бюро статистики: среди населения США,
                            которые дома разговаривают не на английском языке,
                            22% владеют английским плохо или совсем не говорят
                            на нем, хотя и проживают в языковой среде.
                        </Text>
                        <Grandmother />
                        <H2 as="h2" id="approach" ref={addToRefs}>
                            Подход КЭСПА
                        </H2>
                        <Text as="p">
                            В основе обучения английскому языку в КЭСПА лежит
                            подход, противоположный обозначенному выше. Ученик
                            первым делом учится не расшифровывать «чужое», а
                            наоборот, выражать «свое» средствами «чужого».
                            Используемые слова и конструкции изначально являются
                            средствами общения и самовыражения на родном языке,
                            поэтому и на английском они не превращаются в
                            пассивное, «немое» знание, а остаются в памяти и
                            легко воспроизводятся в нужный момент.
                        </Text>
                        <Text as="p">
                            Этот принцип заложен и в названии школы. Тогда как
                            традиционное обучение чаще ставит перед учеником
                            вопрос «Как это переводится с английского?», в КЭСПА
                            главный вопрос – «Как это сказать по-английски?».
                            Первые буквы в этой фразе и сложились в название
                            школы – КЭСПА.
                        </Text>
                        <H2 as="h2" id="five-principles" ref={addToRefs}>
                            Пять принципов преподавания в КЭСПА
                        </H2>
                        <Text as="p">
                            В основу преподавания в КЭСПА заложены пять
                            принципов, которые призваны реализовать главную
                            задачу школы – научить выражать свое средствами
                            чужого языка.
                        </Text>
                        <H3 as="h3">
                            Первый принцип: от родного к иностранному
                        </H3>
                        <Text as="p">
                            Обучение чужому языку по методу КЭСПА вне языковой
                            среды строится на отталкивании от родного языка, а
                            не наоборот. Это базовый принцип нашей школы.
                        </Text>
                        <H3 as="h3">Второй принцип: ПЛФ</H3>
                        <Text as="p">
                            Как отталкиваться от всего родного языка, ведь он
                            слишком необъятен? Можно упростить задачу, выделив
                            речевые структуры, которые принято употреблять в
                            определенной жизненной ситуации, –{' '}
                            <b>
                                прагмалингвистические формулы (сокращенно – ПЛФ)
                            </b>
                            .
                        </Text>
                        <HighlightedText as="p">
                            Прагмалингвистика или лингвистическая прагматика –
                            это область языкознания, которая изучает отношения
                            между языковыми единицами и условиями их
                            употребления в процессе речевого взаимодействия.
                        </HighlightedText>
                        <Text as="p">
                            ПЛФ – жесткая грамматическая конструкция со сменной
                            смысловой составляющей. Такой переменной может
                            выступать любая часть речи, например, глагол:
                        </Text>
                        <ListWithDash>
                            <li>
                                Я умею делать это (читать, писать, говорить
                                по-английски, водить машину)
                            </li>
                            <li>
                                Я делаю это каждый день (пью кофе, слушаю
                                музыку, выгуливаю собаку)
                            </li>
                        </ListWithDash>
                        <Text as="p">
                            Сменными смысловыми составляющими ПЛФ могут быть не
                            только части речи. Например, в ПЛФ с глаголом
                            долженствования «Я должен сделать это» сменной
                            составляющей является мотивация говорящего, то есть
                            его отношение к одному и тому же действию в разных
                            жизненных ситуациях:
                            <br />– «Я должен это сделать!» – говорит человек и
                            в том случае, когда он что-то сам себе приказывает;
                            и в том случае, когда он вынужден что-то сделать в
                            связи с неблагоприятно сложившимися
                            обстоятельствами; и в том случае, когда он заранее
                            запланировал какое-то действие или договорился с
                            кем-то о чем-нибудь; и в том случае, когда он
                            поступает в соответствии с принятыми в обществе
                            нормами поведения.
                        </Text>
                        <Text as="p">
                            В английском языке существует несколько смысловых
                            эквивалентов русской ПЛФ «Я должен сделать это»:
                        </Text>
                        <ListWithDash>
                            <li>
                                I must do it. (приказ самому себе, возможно, в
                                целях самовоспитания)
                            </li>
                            <li>
                                I have to do it. (конкретные обстоятельства
                                вынуждают)
                            </li>
                            <li>
                                I am to do it. (должен, так как уже заранее
                                договорился с кем-то)
                            </li>
                            <li>
                                I ought to do it. (должен так поступить, потому
                                что так принято в обществе)
                            </li>
                        </ListWithDash>
                        <Text>
                            Несмотря на жесткую структуру, прагмалингвистические
                            формулы мобильны, так как в качестве сменной
                            смысловой составляющей могут выступать разные
                            элементы одной и той же грамматической конструкции.
                            Например, в ПЛФ «Я должен сделать это» в качестве
                            сменной смысловой составляющей могут выступать
                            глагол («Я должен сделать [починить, написать,
                            приготовить] это»), местоимение («Я [ты, она, мой
                            брат, ваши дети] должен сделать это»), мотивация
                            говорящего (в границах одной модальности
                            долженствования) – см. пример выше.
                        </Text>
                        <H3 as="h3">
                            Третий принцип: коммуникативный набор ПЛФ
                        </H3>
                        <Text as="p">
                            В родном языке каждый человек пользуется
                            определенным количеством прагмалингвистических
                            формул, или коммуникативным набором ПЛФ. Их
                            количество ограниченно, так как и грамматические
                            структуры языка не бесконечны. Порядка 400 ПЛФ
                            достаточно, чтобы генерировать любое количество
                            предложений во всех ситуациях.
                        </Text>
                        <Text as="p">
                            Отсюда ясно, как в нашей школе формируются
                            программы:
                        </Text>
                        <ListWithDash>
                            <li>
                                <b>Уверенный турист</b> – около 90 ПЛФ,
                                соответствует A1
                            </li>
                            <li>
                                <b>Разговорчивый турист</b> – 180 ПЛФ, A2
                            </li>
                            <li>
                                <b>Приятный собеседник</b> – 280 ПЛФ, B1
                            </li>
                            <li>
                                <b>Свободно общающийся</b> – 335 ПЛФ, B2
                            </li>
                            <li>
                                <b>Почти носитель</b> – 390 ПЛФ, C1.
                            </li>
                        </ListWithDash>
                        <H3 as="h3">Четвертый принцип: грамматика в мышцы</H3>
                        <Text as="p">
                            Мы уже говорили, что память – непростая штука.
                            Просто составлять предложения на английском
                            недостаточно. Нужно, чтобы они отложились в
                            «артикуляционной памяти» – моторно-двигательной
                            памяти на произношение словосочетаний и
                            грамматических конструкций. Иными словами,
                            произношение тех или иных конструкций нужно
                            запомнить на уровне мышц языка и губ, голосовых
                            связок, тогда воспроизвести их не составит труда
                            даже спустя несколько месяцев. В КЭСПА используется
                            специальная система упражнений, направленных на
                            развитие артикуляционной памяти.{' '}
                        </Text>
                        <HighlightedText as="p">
                            В основу системы артикуляционно-грамматических
                            упражнений легли{' '}
                            <a href="http://avtodidaktika.com/">
                                разработки Валерия Куринского
                            </a>
                            . Он заметил, что простое механическое повторение не
                            дает такого же эффекта, как повторение «творческое».
                            Изменение интонации, использование дополнительных
                            жестов и другие приемы позволяют запоминать слова и
                            фразы так, чтобы они отскакивали от зубов. Ученик
                            после таких упражнений говорит по-английски, не
                            задумываясь – так же, как, например, опытный
                            водитель не размышляет о том, какую передачу ему
                            включить, а делает это на автомате.
                        </HighlightedText>
                        <Lips />
                        <H3 as="h3">Пятый принцип: лингвотипы</H3>
                        <Text as="p">
                            Люди разные. Этот простой и всем известный факт
                            обычно игнорируется при обучении, особенно
                            групповом, тем самым эффективность обучения заведомо
                            снижается. Мы в КЭСПА такой ошибки не допускаем.
                        </Text>
                        <Text as="p">
                            У каждого человека есть свои коммуникативные
                            привычки: кто-то говорит очень быстро и активно
                            жестикулирует, кто-то говорит невнятно и тихо. Это
                            не хорошо и не плохо – это просто говорит о выборе
                            человеком определенных речевых средств для выражения
                            своих мыслей и эмоций. Еще до начала обучения в
                            КЭСПА мы определяем речевые особенности каждого
                            ученика и составляем его «лингвопортрет», или
                            определяем «лингвотип». В отличие от психотипа
                            (сангвиник, холерик, флегматик, меланхолик),
                            лингвотип может меняться в зависимости от конкретной
                            жизненной ситуации и по мере освоения языка.
                        </Text>
                        <Text as="p">
                            Наш лингвопортрет описывает ученика по следующим
                            параметрам:
                        </Text>
                        <ListWithDash>
                            <li>Скорость и внятность речи (на родном языке)</li>
                            <li>
                                Инициативность в разговоре (охотный или
                                вынужденный собеседник)
                            </li>
                            <li>
                                Склонность использовать сложную или простую
                                лексику (речевой максималист или минималист)
                            </li>
                            <li>
                                Ритмическая отзывчивость, т.е. насколько хорошо
                                человек слышит и имитирует чужую речь
                            </li>
                        </ListWithDash>
                        <Text as="p">
                            В зависимости от результатов мы подбираем наиболее
                            подходящего преподавателя, который дает ученику
                            только те упражнения, которые соответствуют его
                            лингвопортрету. Например, если человек – речевой
                            минималист, то есть предпочитает говорить кратко и
                            по делу, преподаватель будет давать ему упражнения и
                            тексты с минимумом лексики и основной грамматикой. А
                            для речевых максималистов упражнения будут совсем
                            другими – с метафорами, сложными предложениями.
                        </Text>
                        <Talk />
                        <H2 as="h2" id="finally" ref={addToRefs}>
                            И напоследок...
                        </H2>
                        <Text as="p">
                            Как вообще возникли принципы школы КЭСПА? Этому
                            предшествовали исследования и наблюдения лингвиста
                            Инны Гивенталь. О них рассказывает сама
                            исследователь и по совместительству создатель
                            методики КЭСПА:
                        </Text>
                        <VideoWrap>
                            <iframe
                                title="Инна Гивенталь о метдоике КЭСПА"
                                width="100%"
                                height="100%"
                                src="https://www.youtube.com/embed/aKl0vE3xcpc"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </VideoWrap>
                    </NewContentWrapper>
                </NewPageWrapper>
                <CustomBreadCrumbs crumbs={crumbs} />
            </Container>

            <Footer />
        </Wrap>
    );
};

export default MethodologyPage;
